import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  api,
  type ApiError,
  type GetAgentResult,
  type ListAgentToolsResult,
  type PublishAgentResult,
} from '@whinerp/client';
import { Button, ErrorState, Icon, Num, PageHeading, Panel, SectionLabel, Skeleton } from '@whinerp/ui';
import { formatCount, formatMoney } from '@whinerp/contracts/format';
import { useErrorMessage, useFormat, useT } from '../../app/i18n.js';
import { defineScreen } from '../types.js';

/** The records an agent can be grounded on, mirroring core's closed list. */
const GROUNDABLE_RECORDS = ['account', 'contact', 'deal', 'lead'] as const;

/**
 * SC-25 Agent builder.
 *
 * The definition and the test chat on one screen, deliberately. An agent is
 * built by changing an instruction and asking again, and a builder that made
 * somebody navigate between the two would have them change three things before
 * testing - at which point they cannot tell which one helped.
 *
 * Two things it insists on.
 *
 * **Autonomy is set per action, and the control says what each level means.**
 * "Propose, and wait" rather than "propose": the word alone does not tell
 * somebody that nothing will happen until they look.
 *
 * **What a test cost is shown beside the answer.** Cost discovered on a
 * monthly invoice is cost discovered too late, and the builder is where
 * somebody runs the same prompt twenty times.
 */

type Agent = GetAgentResult;
type Tools = ListAgentToolsResult;
/** Exactly what the publish route says stops it. No cast needed. */
type Blocker = PublishAgentResult['blockedBy'][number];

const AUTONOMY = ['propose', 'auto', 'read-only'] as const;

function AgentBuilderScreen(): JSX.Element {
  const t = useT();
  const errorMessage = useErrorMessage();
  const format = useFormat();
  const queryClient = useQueryClient();
  const params = useParams();
  /*
   * `/agents/new` creates one; anything else opens it.
   *
   * The same convention the report and segment builders use. Without it there
   * was no way to create an agent anywhere in the product: `saveAgent` accepts
   * a null id and nothing ever sent one, so the agents list had an empty state
   * with no action it could honestly offer.
   */
  const creating = (params['id'] ?? '') === 'new';
  const agentId = creating ? '' : (params['id'] ?? '');
  const [prompt, setPrompt] = useState('');
  const [blocked, setBlocked] = useState<Blocker[] | null>(null);

  const agent = useQuery<Agent, ApiError>({
    queryKey: ['agent', agentId],
    queryFn: () => api.getAgent({ path: { agentId } }),
    enabled: !creating,
  });

  const tools = useQuery<Tools, ApiError>({
    queryKey: ['agent-tools'],
    queryFn: () => api.listAgentTools({}),
  });

  const test = useMutation({
    mutationFn: () => api.testAgent({ path: { agentId }, body: { prompt } }),
  });

  /*
   * Grounding (F-108).
   *
   * Sources were shown by this screen and read by nothing at run time, so a
   * ticked box was a claim the product did not keep. They are editable here,
   * and the preview answers the question somebody actually has when they tick
   * one: would it find the right thing?
   */
  const [question, setQuestion] = useState('');
  const sources = agent.data?.agent.sources ?? [];
  const has = (kind: string, reference: string | null): boolean =>
    sources.some((source) => source.kind === kind && (source.reference ?? null) === reference && source.enabled);
  const saveSources = useMutation({
    mutationFn: (next: Array<{ kind: 'knowledge' | 'records' | 'documents'; reference: string | null; enabled: boolean }>) =>
      api.setAgentSources({ path: { agentId }, body: { sources: next } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['agent', agentId] }),
  });
  const toggle = (kind: 'knowledge' | 'records' | 'documents', reference: string | null): void => {
    const without = sources
      .filter((source) => !(source.kind === kind && (source.reference ?? null) === reference))
      .map((source) => ({ kind: source.kind as 'knowledge', reference: source.reference, enabled: source.enabled }));
    saveSources.mutate(has(kind, reference) ? without : [...without, { kind, reference, enabled: true }]);
  };
  const reindex = useMutation({ mutationFn: () => api.reindexGrounding({ body: {} }) });
  const preview = useMutation({
    mutationFn: () => api.previewGrounding({ path: { agentId }, body: { question } }),
  });

  /*
   * The instructions, saved on blur.
   *
   * `saveAgent` takes the whole definition, so this reads the current one out
   * of the cache rather than keeping a second copy in state: two copies of an
   * agent's name and purpose would be two answers to what it is called, and
   * the stale one wins whichever way round the race goes.
   */
  const saveInstructions = useMutation({
    mutationFn: (instructions: string) => {
      const current = queryClient.getQueryData<Agent>(['agent', agentId])?.agent;
      if (!current) throw new Error('The agent is still loading.');
      return api.saveAgent({
        body: {
          agentId,
          key: current.key,
          name: current.name,
          purpose: current.purpose,
          instructions,
          model: current.model,
        },
      });
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['agent', agentId] }),
  });

  const setAutonomy = useMutation({
    mutationFn: (input: { tool: string; autonomy: 'propose' | 'auto' | 'read-only' }) =>
      api.setAgentAutonomy({ path: { agentId }, body: { ...input, enabled: true } }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['agent', agentId] }),
  });

  const runTests = useMutation({
    mutationFn: () => api.runAgentTests({ path: { agentId } }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['agent', agentId] }),
  });

  const publish = useMutation({
    mutationFn: () => api.publishAgent({ path: { agentId }, body: { status: 'active' } }),
    onSuccess: (result) => {
      // The refusal carries the list. An agent published with a failing test
      // has known bad behaviour live, and the test was written by whoever is
      // publishing - so the list is the most actionable thing to say.
      setBlocked(result.blockedBy.length > 0 ? result.blockedBy : null);
      void queryClient.invalidateQueries({ queryKey: ['agent', agentId] });
    },
  });

  if (creating) return <NewAgent />;
  if (agent.isError) return <BuilderError error={agent.error} retry={() => void agent.refetch()} />;
  if (agent.isPending) return <BuilderLoading />;

  const definition = agent.data.agent;
  const chosen = new Map(definition.actions.map((action) => [action.tool, action]));

  return (
    <div data-component="AgentBuilder" className="flex w-full min-w-0 flex-col gap-3">
      <header className="flex flex-wrap items-center gap-3">
        <PageHeading>{definition.name}</PageHeading>
        <span
          data-agent-status={definition.status}
          className="inline-flex h-chip items-center rounded-chip bg-sunken px-2 text-meta text-ink-600"
        >
          {definition.status}
        </span>
        <span className="ms-auto flex gap-2">
          <Button intent="secondary" data-run-tests busy={runTests.isPending} onClick={() => runTests.mutate()}>
            {t('agent.runTests')}
          </Button>
          <Button intent="primary" data-publish busy={publish.isPending} onClick={() => publish.mutate()}>
            {t('agent.publish')}
          </Button>
        </span>
      </header>

      {blocked ? (
        <div role="alert" data-publish-blocked className="flex flex-col gap-1 rounded-card bg-warning-tint px-3 py-2 text-dense text-warning-text">
          <span>{t('agent.blocked')}</span>
          <ul className="flex flex-col gap-0.5">
            {blocked.map((entry) => (
              <li key={`${entry.testId}-${entry.question}`}>
                <Icon name="Warning" size="inline" decorative /> {entry.question} — {entry.detail}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="flex min-w-0 gap-4">
        {/* Left: topics. What it is meant to be asked about. */}
        <aside aria-label={t('agent.topics')} data-pane="topics" className="flex w-panel-narrow shrink-0 flex-col gap-2">
          <SectionLabel>{t('agent.topics')}</SectionLabel>
          <ul data-topics className="flex flex-col gap-1">
            {definition.topics.map((topic) => (
              <li key={topic.topicId} data-topic={topic.topicId} className="rounded-control border border-rule p-2">
                <span className="block text-dense text-ink">{topic.name}</span>
                <span className="block text-meta text-ink-meta">{topic.description}</span>
              </li>
            ))}
          </ul>
        </aside>

        {/* Centre: the instructions and the guardrails. */}
        <section aria-label={t('agent.instructions')} data-pane="definition" className="flex min-w-0 flex-1 flex-col gap-3">
          <div className="flex flex-col gap-1">
            <SectionLabel>{t('agent.purpose')}</SectionLabel>
            <p className="text-body text-ink">{definition.purpose}</p>
          </div>
          <label className="flex flex-col gap-1 text-dense text-ink">
            {t('agent.instructions')}
            <textarea
              data-instructions
              aria-label={t('agent.instructions')}
              defaultValue={definition.instructions}
              /*
               * Saved on blur. It had no handler at all: eight rows somebody
               * could type a whole agent's instructions into, which were
               * discarded the moment the screen re-rendered. Uncontrolled and
               * saved on leaving, so a long instruction is not a request per
               * keystroke.
               */
              onBlur={(event) => {
                if (event.target.value === definition.instructions) return;
                saveInstructions.mutate(event.target.value);
              }}
              rows={8}
              className="rounded-control border border-rule bg-surface px-2 py-1 text-body text-ink"
            />
            {saveInstructions.isError ? (
              <span data-instructions-refusal className="text-meta text-danger-text">
                {errorMessage(saveInstructions.error)}
              </span>
            ) : null}
          </label>

          <div className="flex flex-col gap-1">
            <SectionLabel>{t('agent.guardrails')}</SectionLabel>
            <ul data-guardrails className="flex flex-col gap-1">
              {definition.guardrails.map((guardrail) => (
                <li
                  key={guardrail.guardrailId}
                  data-guardrail={guardrail.kind}
                  data-guardrail-enforcement={guardrail.enforcement}
                  className="flex flex-wrap items-baseline gap-2 text-dense text-ink"
                >
                  {/* The rule in words. It rendered as its stored key -
                      "no-discount" - which is a value, not a sentence. */}
                  <span>
                    {t(`guardrail.${guardrail.kind}` as 'guardrail.no-discount')}
                    {guardrail.value ? `: ${guardrail.value}` : ''}
                  </span>
                  {/* Whether it is a promise or a request. A rule the engine
                      checks and one it can only state are different things,
                      and showing them identically is how somebody comes to
                      believe the second is the first. */}
                  <span
                    title={t(
                      guardrail.enforcement === 'enforced'
                        ? 'guardrail.enforced.help'
                        : 'guardrail.instructed.help',
                    )}
                    className={
                      guardrail.enforcement === 'enforced'
                        ? 'rounded-chip bg-success-tint px-1.5 text-meta text-success-text'
                        : 'rounded-chip bg-sunken px-1.5 text-meta text-ink-600'
                    }
                  >
                    {t(
                      guardrail.enforcement === 'enforced'
                        ? 'guardrail.enforced'
                        : 'guardrail.instructed',
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <div className="flex flex-col gap-1" data-region="grounding">
            <div className="flex flex-wrap items-center gap-2">
              <SectionLabel>{t('agent.sources')}</SectionLabel>
              <Button intent="ghost" size="small" data-reindex disabled={reindex.isPending} onClick={() => reindex.mutate()}>
                {t('agent.reindex')}
              </Button>
              {reindex.data ? (
                <span data-index-state={reindex.data.mode} className="text-meta text-ink-meta">
                  {t(reindex.data.mode === 'live' ? 'agent.index.meaning' : reindex.data.mode === 'sandbox' ? 'agent.index.words' : 'agent.index.none', {
                    values: { indexed: String(reindex.data.indexed), skipped: String(reindex.data.skipped) },
                  })}
                </span>
              ) : null}
            </div>
            {/* A scope per line, ticked or not. What an agent may read is the
                shortest description of what it can leak, so it is a list a
                person reads rather than a setting behind a menu. */}
            <ul data-sources className="flex flex-col gap-0.5">
              {([
                { kind: 'knowledge' as const, reference: null, label: t('agent.source.knowledge') },
                { kind: 'documents' as const, reference: null, label: t('agent.source.documents') },
                ...GROUNDABLE_RECORDS.map((key) => ({
                  kind: 'records' as const,
                  reference: key,
                  label: t(`agent.source.record.${key}` as 'agent.source.record.deal'),
                })),
              ]).map((source) => (
                <li key={`${source.kind}:${source.reference ?? ''}`}>
                  <label data-source={`${source.kind}:${source.reference ?? ''}`} className="flex items-center gap-2 text-dense text-ink">
                    <input
                      type="checkbox"
                      checked={has(source.kind, source.reference)}
                      disabled={saveSources.isPending}
                      onChange={() => toggle(source.kind, source.reference)}
                    />
                    {source.label}
                  </label>
                </li>
              ))}
            </ul>
            {saveSources.isError ? (
              <span data-sources-refusal className="text-meta text-danger-text">{errorMessage(saveSources.error)}</span>
            ) : null}
            {/* Would it find the right thing? Answered without spending a
                model call to find out. */}
            <div className="flex flex-wrap items-center gap-2">
              <input
                data-grounding-question
                aria-label={t('agent.preview.ask')}
                placeholder={t('agent.preview.ask')}
                value={question}
                onChange={(event) => setQuestion(event.target.value)}
                className="min-w-0 flex-1 rounded-control border border-rule bg-surface px-2 py-1 text-body text-ink"
              />
              <Button
                intent="secondary"
                size="small"
                data-grounding-preview
                disabled={preview.isPending || question.trim() === ''}
                onClick={() => preview.mutate()}
              >
                {t('agent.preview')}
              </Button>
            </div>
            {preview.data ? (
              preview.data.citations.length === 0 ? (
                <p data-grounding-empty className="text-meta text-ink-meta">{t('agent.preview.nothing')}</p>
              ) : (
                <ol data-grounding-results className="flex flex-col gap-1">
                  {preview.data.citations.map((citation) => (
                    <li key={`${citation.objectKey}:${citation.sourceId}`} data-citation={citation.title} className="flex flex-col">
                      {/* By title, because a citation a reader cannot go and
                          check is decoration. */}
                      <span className="text-dense text-ink">{citation.title}</span>
                      <span className="text-meta text-ink-meta">{citation.passage.slice(0, 160)}</span>
                    </li>
                  ))}
                </ol>
              )
            ) : null}
          </div>

          <div className="flex flex-col gap-1">
            <SectionLabel>{t('agent.tests')}</SectionLabel>
            <ul data-tests className="flex flex-col gap-0.5">
              {definition.tests.map((entry) => (
                <li
                  key={entry.testId}
                  data-test={entry.testId}
                  data-test-outcome={entry.lastOutcome ?? 'not-run'}
                  className="flex items-baseline justify-between gap-2 text-dense text-ink"
                >
                  <span>{entry.question}</span>
                  <span className="text-meta text-ink-meta">{entry.lastOutcome ?? 'not run'}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Right: what it may do, one autonomy per action. */}
        <aside aria-label={t('agent.actions')} data-pane="actions" className="flex w-panel-wide shrink-0 flex-col gap-2">
          <SectionLabel>{t('agent.actions')}</SectionLabel>
          <ul data-actions className="flex flex-col gap-2">
            {(tools.data?.tools ?? []).map((tool) => {
              const action = chosen.get(tool.name);
              return (
                <li key={tool.name} data-tool={tool.name} className="flex flex-col gap-1">
                  <span className="flex items-baseline gap-2">
                    <span className="text-dense text-ink">{tool.name}</span>
                    {/* Locked rather than hidden: hiding it means nobody
                        discovers the reason to switch the module on. */}
                    {tool.available ? null : (
                      <span data-tool-locked={tool.name} className="text-meta text-warning-text">
                        <Icon name="Lock" size="inline" decorative />{' '}
                        {t('agent.locked', { values: { entitlement: tool.entitlement } })}
                      </span>
                    )}
                  </span>
                  {tool.autonomy === 'read-only' ? (
                    /*
                     * A read-only tool states its level; it does not offer it.
                     *
                     * This was a disabled select holding a single option,
                     * which is a control that looks like a choice and is not.
                     * A tool that can only read says so in words - and the
                     * sentence is the same one the option carried, because
                     * "read-only" alone does not tell anybody what it means.
                     */
                    <span data-autonomy={tool.name} data-autonomy-fixed className="text-meta text-ink-meta">
                      {t('agent.autonomy.read-only')}
                    </span>
                  ) : (
                    <select
                      data-autonomy={tool.name}
                      aria-label={t('agent.autonomy')}
                      disabled={!tool.available}
                      value={action?.autonomy ?? 'propose'}
                      onChange={(event) =>
                        setAutonomy.mutate({
                          tool: tool.name,
                          autonomy: event.target.value as 'propose',
                        })
                      }
                      className="h-input rounded-control border border-rule bg-surface px-2 text-dense text-ink"
                    >
                      {AUTONOMY.map((level) => (
                        <option key={level} value={level}>
                          {/* The sentence, not the word: "propose" alone does
                              not tell somebody nothing happens until they look. */}
                          {t(`agent.autonomy.${level}` as 'agent.autonomy.propose')}
                        </option>
                      ))}
                    </select>
                  )}
                </li>
              );
            })}
          </ul>
        </aside>
      </div>

      {/* Docked: the test chat, on the same screen as the definition. */}
      <Panel>
        <div data-pane="test-chat" className="flex flex-col gap-2">
          <SectionLabel>{t('agent.testChat')}</SectionLabel>
          <label className="flex flex-col gap-1 text-dense text-ink">
            <span className="sr-only">{t('agent.ask')}</span>
            <input
              data-test-prompt
              aria-label={t('agent.ask')}
              value={prompt}
              onChange={(event) => setPrompt(event.target.value)}
              className="h-input rounded-control border border-rule bg-surface px-2 text-body text-ink"
            />
          </label>
          <span>
            <Button intent="secondary" data-ask busy={test.isPending} onClick={() => test.mutate()}>
              {t('agent.send')}
            </Button>
          </span>

          {test.data ? (
            <div data-test-result className="flex flex-col gap-1">
              <p className="text-body text-ink">{test.data.answer ?? ''}</p>
              {/* What it cost, here, where somebody runs the same prompt
                  twenty times. */}
              <Num data-test-cost className="text-meta text-ink-meta">
                {t('agent.cost', {
                  values: {
                    amount: formatMoney(test.data.cost.amount, format, {
                      currency: test.data.cost.currency,
                    }),
                  },
                })}
                {' · '}
                {t('agent.tokens', {
                  values: {
                    input: formatCount(test.data.cost.inputTokens, format),
                    output: formatCount(test.data.cost.outputTokens, format),
                  },
                })}
              </Num>
              <ol data-test-trace className="flex flex-col gap-0.5">
                {test.data.steps.map((step) => (
                  <li key={step.sequence} className="text-meta text-ink-meta">
                    {step.summary}
                  </li>
                ))}
              </ol>
            </div>
          ) : null}
        </div>
      </Panel>
    </div>
  );
}

function BuilderEmpty(): JSX.Element {
  const t = useT();
  return <p className="text-body text-ink-meta">{t('agent.empty.body')}</p>;
}

/**
 * Creating an agent.
 *
 * Three fields, because three is what `saveAgent` needs and a longer form
 * before anything exists is a wall in front of the thing somebody came to do.
 * Instructions, tools and guardrails are all set on the builder afterwards,
 * where there is an agent for them to belong to.
 *
 * The key is separate from the name and said to be permanent, because it is:
 * automations refer to an agent by it, and a key that changed would break
 * them silently.
 */
function NewAgent(): JSX.Element {
  const t = useT();
  const errorMessage = useErrorMessage();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [key, setKey] = useState('');
  const [purpose, setPurpose] = useState('');

  const create = useMutation({
    mutationFn: () =>
      api.saveAgent({
        body: { agentId: null, key: key.trim(), name: name.trim(), purpose: purpose.trim(), instructions: '' },
      }),
    // Straight into the builder for the agent just made, rather than back to a
    // list where somebody has to find it.
    onSuccess: (result) => navigate(`/agents/${result.agentId}`),
  });

  const ready = name.trim() !== '' && key.trim() !== '' && purpose.trim() !== '';

  return (
    <div data-component="AgentBuilder" data-new-agent className="flex w-full max-w-reading flex-col gap-4">
      <PageHeading kicker={t('nav.agentsList')}>{t('agent.new.title')}</PageHeading>
      <p className="text-body text-ink-600">{t('agent.new.body')}</p>

      <form
        className="flex flex-col gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          create.mutate();
        }}
      >
        <label className="flex flex-col gap-1 text-dense text-ink">
          {t('agent.new.name')}
          <input
            data-agent-name
            value={name}
            onChange={(event) => setName(event.target.value)}
            className="h-input rounded-control border border-control bg-surface px-2 text-body text-ink"
          />
        </label>
        <label className="flex flex-col gap-1 text-dense text-ink">
          {t('agent.new.key')}
          <input
            data-agent-key
            value={key}
            onChange={(event) => setKey(event.target.value)}
            className="h-input rounded-control border border-control bg-surface px-2 text-body text-ink"
          />
          <span className="text-meta text-ink-meta">{t('agent.new.keyHelp')}</span>
        </label>
        <label className="flex flex-col gap-1 text-dense text-ink">
          {t('agent.purpose')}
          <textarea
            data-agent-purpose
            rows={3}
            value={purpose}
            onChange={(event) => setPurpose(event.target.value)}
            className="rounded-control border border-control bg-surface px-2 py-1 text-body text-ink"
          />
        </label>
        <div className="flex items-center gap-2">
          <Button
            type="submit"
            intent="primary"
            data-agent-create
            busy={create.isPending}
            disabled={!ready}
          >
            {t('agent.new.create')}
          </Button>
          {create.isError ? (
            <span data-agent-refusal className="text-dense text-danger-text">
              {errorMessage(create.error)}
            </span>
          ) : null}
        </div>
      </form>
    </div>
  );
}

function BuilderLoading(): JSX.Element {
  const t = useT();
  return (
    <div className="flex w-full gap-4" role="status" aria-live="polite" aria-busy>
      <span className="sr-only">{t('state.loading')}</span>
      <Skeleton className="h-plot w-panel-narrow" />
      <Skeleton className="h-plot flex-1" />
      <Skeleton className="h-plot w-panel-wide" />
    </div>
  );
}

function BuilderError({ error, retry }: { error: unknown; retry: () => void }): JSX.Element {
  return (
    <ErrorState
      title="This agent could not be loaded"
      body={error instanceof Error ? error.message : 'The agent did not load.'}
      onRetry={retry}
    />
  );
}

export const screen = defineScreen({
  id: 'SC-25',
  route: '/agents/:id',
  surface: 'page',
  title: 'Agent builder',
  /*
   * F-109 is claimed now, and the claim is narrower than the feature's name.
   *
   * The guardrails were stored, edited and shown, and the reasoning loop never
   * consulted one - so the claim was withdrawn and a tripwire kept it
   * withdrawn. The loop reads them now: four kinds are checked by the engine
   * and refused when broken, and two are conditions in English that go into
   * the prompt and nothing can verify.
   *
   * The screen says which is which, because somebody setting "refuse to
   * discuss redundancies" is entitled to know whether they have bought a
   * guarantee or a request.
   */
  features: ['F-105', 'F-107', 'F-109', 'F-113'],
  entitlement: 'F-105',
  reachedBy: [{ kind: 'nav', item: 'agents' }],
  components: [],
  elements: ['AgentBuilder'],
  sampleRecord: { objectKey: 'agent', param: 'id' },
  states: { empty: BuilderEmpty, loading: BuilderLoading, error: BuilderError },
  component: AgentBuilderScreen,
});

export default screen;
